import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Send, Receipt } from "lucide-react";
import { adminClient } from "./api";
import { promoDiscount } from "@/components/site/PromoField";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function InvoiceBuilder({ booking, onSaved, endpoint }) {
  const existing = booking.invoice;
  const isOrder = Array.isArray(booking.items) && !booking.service_type;
  const [items, setItems] = useState(existing?.items?.length ? existing.items : isOrder
    ? [...booking.items.map((i) => ({ label: `${i.quantity}× ${i.title}${i.size ? ` (${i.size})` : ""}`, amount: Number(i.price) * Number(i.quantity || 1) })), ...(Number(booking.shipping_fee) > 0 ? [{ label: "Shipping", amount: Number(booking.shipping_fee) }] : [])]
    : [{ label: `${String(booking.service_type || "Service").replace(/^\w/, (c) => c.toUpperCase())} — labor & crew`, amount: booking.firm_price || booking.price_high || "" }]);
  const initialSubtotal = (existing?.items?.length ? existing.items : items).reduce((s, i) => s + Number(i.amount || 0), 0);
  const [discount, setDiscount] = useState(existing ? (existing.discount || 0) : (booking.promo && !isOrder ? promoDiscount(booking.promo, initialSubtotal) : 0));
  const [notes, setNotes] = useState(existing?.notes || "");
  const [busy, setBusy] = useState(false);

  const subtotal = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const total = Math.max(0, subtotal - Number(discount || 0));
  const paid = Number(booking.money?.paid || 0);
  const balance = Math.max(0, total - paid);

  const setItem = (n, k, v) => setItems(items.map((it, i) => (i === n ? { ...it, [k]: v } : it)));
  const addItem = () => setItems([...items, { label: "", amount: "" }]);
  const removeItem = (n) => setItems(items.filter((_, i) => i !== n));

  const save = async (sendEmail) => {
    const clean = items.filter((i) => i.label.trim()).map((i) => ({ label: i.label, amount: Number(i.amount || 0) }));
    if (!clean.length) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      const { data } = await adminClient.put(endpoint || `/admin/bookings/${booking.id}/invoice`, { items: clean, discount: Number(discount || 0), notes, send_email: sendEmail });
      const rec = data.booking || data.order;
      toast.success(sendEmail ? (data.email?.status === "sent" ? `Invoice v${rec.invoice.version} emailed to ${booking.email}` : `Saved — email ${data.email?.status || "skipped"}`) : "Invoice saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save invoice"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="invoice-builder">
      <div className="space-y-2">
        {items.map((it, n) => (
          <div key={n} className="grid grid-cols-[1fr_100px_28px] gap-2 items-center" data-testid={`invoice-item-${n}`}>
            <Input data-testid={`invoice-item-label-${n}`} value={it.label} onChange={(e) => setItem(n, "label", e.target.value)} placeholder="Line item (e.g. Mattress disposal)" />
            <Input data-testid={`invoice-item-amount-${n}`} type="number" step="0.01" value={it.amount} onChange={(e) => setItem(n, "amount", e.target.value)} className="font-mono-data text-right" placeholder="0" />
            <button data-testid={`invoice-item-remove-${n}`} onClick={() => removeItem(n)} className="text-zinc-400 hover:text-red-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <Button data-testid="invoice-add-item-btn" size="sm" variant="outline" onClick={addItem} className="rounded-md text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Add line</Button>
      </div>
      <div className="grid grid-cols-[1fr_100px] gap-2 items-center">
        <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 text-right pr-2">Discount</Label>
        <Input data-testid="invoice-discount-input" type="number" step="0.01" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} className="font-mono-data text-right" />
      </div>
      {booking.promo && !isOrder && (
        <div className="flex items-center justify-end gap-2 text-[11px] text-emerald-700" data-testid="invoice-promo-hint">
          <span>Promo {booking.promo.code} ({booking.promo.label}) = −{money(promoDiscount(booking.promo, subtotal))} on this subtotal</span>
          <button type="button" data-testid="invoice-apply-promo-btn" onClick={() => setDiscount(promoDiscount(booking.promo, subtotal))} className="font-bold underline">Apply</button>
        </div>
      )}
      <Textarea data-testid="invoice-notes-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes to customer (optional) — e.g. 'Added second dresser found in garage'" />
      <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3 text-xs space-y-1">
        <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{money(subtotal)}</span></div>
        {Number(discount) > 0 && <div className="flex justify-between"><span className="text-zinc-500">Discount</span><span className="text-emerald-700">−{money(discount)}</span></div>}
        <div className="flex justify-between font-bold"><span>Total</span><span data-testid="invoice-total">{money(total)}</span></div>
        {paid > 0 && <div className="flex justify-between"><span className="text-zinc-500">Paid so far</span><span className="text-emerald-700">−{money(paid)}</span></div>}
        <div className="flex justify-between font-black text-[#0C2E2B] text-sm pt-1 border-t border-zinc-200"><span>Balance due</span><span data-testid="invoice-balance">{money(balance)}</span></div>
      </div>
      <div className="flex gap-2">
        <Button data-testid="invoice-save-btn" size="sm" variant="outline" disabled={busy} onClick={() => save(false)} className="rounded-md text-xs">Save only</Button>
        <Button data-testid="invoice-send-btn" size="sm" disabled={busy || !booking.email} onClick={() => save(true)} className="btn-kinetic rounded-md text-xs">
          <Send className="w-3 h-3 mr-1" /> {existing ? "Save & send updated invoice" : "Save & send invoice"}
        </Button>
      </div>
      {existing?.sent_at && <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Receipt className="w-3 h-3" /> v{existing.version} last sent {new Date(existing.sent_at).toLocaleString()}</p>}
    </div>
  );
}
